/**
 * Shared Axios API Client
 * 
 * Uses httpOnly cookie authentication (withCredentials: true).
 * Auto-redirects to /login on 401 unless endpoint is marked as public.
 * 
 * Usage:
 *   import api, { publicRequest } from '../lib/api';
 *   
 *   // Normal authenticated request
 *   const response = await api.get('/missions');
 *   
 *   // Public endpoint (login/register) - no 401 redirect
 *   const response = await api.post('/auth/login', data, publicRequest({}));
 */
import axios from 'axios';

const ORGANIZATION_SCOPE_HEADER = 'X-Organization-Id';
const ORGANIZATION_SCOPE_ERROR = 'organization_scope_error';
const ORGANIZATION_SCOPE_STORAGE_PREFIX = 'fleetora.activeOrganizationId.';

let activeOrganizationId = null;
let scopeAbortController = new AbortController();
let organizationScopeErrorHandler = null;
let organizationScopeGeneration = 0;

export const getOrganizationScopeStorageKey = (userId) => `${ORGANIZATION_SCOPE_STORAGE_PREFIX}${userId}`;

export const clearPersistedOrganizationScopeForUser = (userId) => {
  if (!userId) return;

  try {
    window.localStorage.removeItem(getOrganizationScopeStorageKey(userId));
  } catch (error) {
    console.warn('Unable to clear the persisted organization scope:', error);
  }
};

export const getApiOrganizationScope = () => activeOrganizationId;

export const setApiOrganizationScope = (organizationId) => {
  if (activeOrganizationId === organizationId) return;

  scopeAbortController.abort();
  scopeAbortController = new AbortController();
  activeOrganizationId = organizationId;
  organizationScopeGeneration += 1;
};

export const clearApiOrganizationScope = () => {
  if (activeOrganizationId === null) {
    scopeAbortController.abort();
    scopeAbortController = new AbortController();
    organizationScopeGeneration += 1;
  } else {
    setApiOrganizationScope(null);
  }
  organizationScopeErrorHandler?.(null);
};

export const setOrganizationScopeErrorHandler = (handler) => {
  organizationScopeErrorHandler = handler;
  return () => {
    if (organizationScopeErrorHandler === handler) {
      organizationScopeErrorHandler = null;
    }
  };
};

const apiClient = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  if (config._skipOrganizationScope === true) {
    return config;
  }

  if (activeOrganizationId) {
    config.headers = config.headers || {};
    config.headers[ORGANIZATION_SCOPE_HEADER] = activeOrganizationId;
  }

  config._organizationScopeGeneration = organizationScopeGeneration;

  if (!config.signal) {
    config.signal = scopeAbortController.signal;
  }

  return config;
});

// Response interceptor: 401 → redirect to /login (unless public endpoint)
const isStaleOrganizationScopeResponse = (config) => (
  config?._skipOrganizationScope !== true &&
  config?._organizationScopeGeneration !== undefined &&
  config._organizationScopeGeneration !== organizationScopeGeneration
);

// A response whose request was issued under a since-superseded organization scope is
// stale: acting on its data (or surfacing its rejection as a real error) would be wrong.
// Most call sites are list/detail fetches that a scope switch naturally re-triggers
// (gated-route remount, or an effect keyed on the active scope) — for those, silently
// never settling the promise is safe: their own fresh request resolves normally and
// drives `loading`/data state. Call sites with no such automatic re-trigger (a one-off
// action tied to its own `saving`/`running` flag) must opt in via
// `settleOrganizationScopeCancel()` so the promise still rejects and `finally` can reset
// that flag; they then use `isOrganizationScopeCancel()` to skip the error toast.
const rejectStaleOrganizationScopeResponse = (config) => {
  if (config?._organizationScopeSettleOnCancel === true) {
    return Promise.reject(new axios.CanceledError('Organization scope changed'));
  }
  return new Promise(() => {});
};

apiClient.interceptors.response.use(
  (response) => {
    if (isStaleOrganizationScopeResponse(response.config)) {
      return rejectStaleOrganizationScopeResponse(response.config);
    }
    return response;
  },
  (error) => {
    if (isStaleOrganizationScopeResponse(error.config)) {
      return rejectStaleOrganizationScopeResponse(error.config);
    }

    const isPublicEndpoint = error.config?._publicEndpoint === true;
    const errorType = error.response?.data?.type;
    const errorTitle = error.response?.data?.title;

    if (
      organizationScopeErrorHandler &&
      (errorTitle === ORGANIZATION_SCOPE_ERROR || errorType?.endsWith(`/${ORGANIZATION_SCOPE_ERROR}`))
    ) {
      organizationScopeErrorHandler(error);
    }
    
    if (error.response?.status === 401 && !isPublicEndpoint) {
      // Cookie expired or invalid — redirect to login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = `${process.env.PUBLIC_URL}/login`;
      }
    }
    
    // Always reject so call-site error handlers still fire
    return Promise.reject(error);
  }
);

/**
 * Helper to mark a request as a public endpoint (no 401 redirect)
 * Use for login, register, logout, and other unauthenticated endpoints.
 * 
 * @param {Object} config - Axios request config
 * @returns {Object} - Config with _publicEndpoint flag
 */
export const publicRequest = (config = {}) => ({
  ...config,
  _publicEndpoint: true,
  _skipOrganizationScope: true,
});

export const withoutOrganizationScope = (config = {}) => ({
  ...config,
  _skipOrganizationScope: true,
});

/**
 * Opt a one-off action request (download/import/send/run — anything driving its own
 * `saving`/`running` flag with no automatic re-trigger on scope change) into settling
 * normally when the organization scope changes mid-flight, instead of the default
 * silent never-settle. Pair with `isOrganizationScopeCancel()` in the `catch` block so
 * the user doesn't see a false error toast, while `finally` still resets the flag.
 */
export const settleOrganizationScopeCancel = (config = {}) => ({
  ...config,
  _organizationScopeSettleOnCancel: true,
});

/** True when `error` is a request cancellation (scope-switch abort or otherwise). */
export const isOrganizationScopeCancel = (error) => axios.isCancel(error);

export default apiClient;
